import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD_BASE =
  'w-full bg-black/40 border border-white/[0.10] focus:border-gold-500/60 rounded-xl px-4 py-3 text-[15px] text-white placeholder:text-white/35 outline-none transition-all';

export default function PartnersLogin() {
  return (
    <LanguageProvider>
      <PartnersLoginInner />
    </LanguageProvider>
  );
}

function PartnersLoginInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const email_label = t('partners.login.field_email');
  const password_label = t('partners.login.field_password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/partners/login`, { email, password });
      localStorage.setItem('nofee_partner_token', data.token);
      toast.success(`${t('partners.login.welcome_back')}, ${data.partner?.organization_name}`);
      navigate('/partners/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.detail || t('partners.login.login_failed');
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      <SEO title="Partner Login — NoFee" description="Sign in to your NoFee Partners dashboard." path="/partners/login" noindex />
      <Navbar forceSolid />

      <main className="pt-32 lg:pt-40 pb-20">
        <div className="max-w-[480px] mx-auto px-6">
          <button
            onClick={() => navigate('/partners')}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/55 hover:text-gold-300 mb-6"
            data-testid="partners-login-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t('partners.login.back')}
          </button>

          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-3">
            {t('partners.login.kicker')}
          </p>
          <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-white leading-[1.05] mb-3">
            {t('partners.login.title')}
          </h1>
          <p className="text-white/55 mb-10">{t('partners.login.subtitle')}</p>

          <form
            onSubmit={onSubmit}
            className="rounded-3xl border border-white/[0.08] bg-gradient-to-b from-zinc-900/50 to-black/30 p-8 space-y-5"
            data-testid="partners-login-form"
          >
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-[0.18em] text-white/55 mb-2">
                {email_label}
              </span>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${FIELD_BASE} pl-11`}
                  placeholder="you@organization.org"
                  data-testid="partners-login-email"
                />
              </div>
            </label>
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-[0.18em] text-white/55 mb-2">
                {password_label}
              </span>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${FIELD_BASE} pl-11`}
                  placeholder="••••••••"
                  data-testid="partners-login-password"
                  autoComplete="current-password"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 h-12 px-6 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 disabled:opacity-60 text-black font-extrabold text-[15px] transition-all"
              data-testid="partners-login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {t('partners.login.submit')} <ArrowRight className="w-4 h-4" />
            </button>

            <p className="text-center text-xs text-white/45 pt-2">
              {t('partners.login.not_partner')}{' '}
              <Link to="/partners/onboarding" className="text-gold-300 hover:text-gold-200 font-semibold">
                {t('partners.login.apply_now')}
              </Link>
            </p>
          </form>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
